import { rtLogService, MonitorEventSeverity, MonitorEventSource } from '@rammetec/core';
import { repositoryFactory, repositoryResolver, DataSourceStrategy, CacheStrategy, createWriteBatch, } from '@rammetec/data-access';
import { getDescriptorForRule } from '../descriptors/descriptorLookup.js';
import { lifecycleService } from './lifecycleService.js';
import { auditService } from './auditService.js';
import { EntityValidationError, } from '../validators/types.js';
import { mergeErrors } from '../validators/mergeErrors.js';
import { runDescriptorValidation } from '../validators/runDescriptorValidation.js';
import { descriptorFieldsForCreateValidation, descriptorFieldsForPartialUpdateValidation, } from '../validators/writableDescriptorFields.js';
import { assertCodeUniquePerOrg } from './assertCodeUniquePerOrg.js';
import { ConcurrentModificationException } from './concurrentModificationException.js';
import { commitWriteBatch } from './commitWriteBatch.js';
import { validationExceptionFromUniqueCodeViolation } from './mapUniqueCodeViolation.js';
import { ValidationException } from './validationException.js';
import { orgEntityConfigService } from './orgEntityConfigService.js';
import { AggregateAuditScope, AuditRecorder, buildAuditSnapshot, diffForAudit, } from './fieldAudit/index.js';
import { logCrudActivity, safeChangedFieldNames } from '../log/activity.js';
export { AggregateAuditScope };
export { EntityValidationError };
export { ConcurrentModificationException };
export { commitWriteBatch };
/** Do not compare, but still increment. System writes (derived status, seed, sync). */
export const VERSION_BYPASS = Symbol('version-bypass');
/**
 * Coerce a persisted / expected version for comparison (spec 0045 / request 0236 §4).
 * PostgresRest returns NUMERIC as string (`"4"`); comparing without this yields false conflicts.
 * Missing → `0` (pre-versioning row). Non-finite / non-integer → throw (corrupt data).
 */
export function toVersion(v) {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
        throw new Error(`Invalid version value: ${String(v)}`);
    }
    return n;
}
export class OrgScopedCrudService {
    constructor(opts) {
        this.opts = opts;
    }
    /**
     * Resolves the repository for the org. Uses {@link repositoryResolver} by default so
     * per-org `config-repository` overrides (e.g. PostgresRest) apply. Pass explicit
     * `dataSource` / `cache` only to bypass the resolver.
     */
    async getRepository(orgId) {
        if (this.opts.dataSource !== undefined || this.opts.cache !== undefined) {
            return repositoryFactory.getRepository(`org/${orgId}/${this.opts.collection}`, this.opts.entity, this.opts.dataSource ?? DataSourceStrategy.Firestore, this.opts.cache ?? CacheStrategy.InMemory);
        }
        return repositoryResolver.resolveWithContext(this.opts.entity, { orgId });
    }
    /**
     * Full collection read. Optional `filterAst` is pushed to the data source via
     * `getAll` (PostgresRest → `/pg`; Firestore applies + completes client-side).
     * Without a filter, behaviour matches the historical unfiltered `getAll`.
     * Always applies {@link OrgScopedCrudOptions.sort} in memory after the fetch.
     */
    async list(orgId, opts) {
        try {
            const repository = await this.getRepository(orgId);
            const items = await repository.getAll(opts?.filterAst ? { filterAst: opts.filterAst } : undefined);
            return this.opts.sort ? [...items].sort(this.opts.sort) : items;
        }
        catch (error) {
            rtLogService.error(`Error fetching ${this.opts.collection} list`, { orgId, collection: this.opts.collection, error });
            throw error;
        }
    }
    /**
     * Server-side filtered read via `getPage` + `FilterAst` (not `getAll` +
     * in-memory filter). No default limit — the caller's AST is the cut.
     *
     * Does **not** apply {@link OrgScopedCrudOptions.sort}; pass `options.sort`
     * so ordering is pushed to the source.
     */
    async query(orgId, options) {
        try {
            const repository = await this.getRepository(orgId);
            const page = await repository.getPage(options);
            return page.items;
        }
        catch (error) {
            rtLogService.error(`Error querying ${this.opts.collection}`, {
                orgId,
                collection: this.opts.collection,
                error,
            });
            throw error;
        }
    }
    /**
     * Filtered count via `getPage` + `countTotal` (not a row fetch).
     *
     * PostgresRest answers with `total` and `limit: 1` so almost no rows travel
     * (`limit: 0` is rejected by `/pg` — "must be a positive integer").
     * If the source omits `total` (e.g. Firestore without a cheap count), falls
     * back to counting the matching page — still AST-cut, still not `getAll`.
     */
    async count(orgId, options) {
        try {
            const repository = await this.getRepository(orgId);
            const counted = await repository.getPage({
                ...options,
                countTotal: true,
                limit: 1,
            });
            if (typeof counted.total === 'number')
                return counted.total;
            const page = await repository.getPage(options);
            return page.items.length;
        }
        catch (error) {
            rtLogService.error(`Error counting ${this.opts.collection}`, {
                orgId,
                collection: this.opts.collection,
                error,
            });
            throw error;
        }
    }
    async getById(orgId, id) {
        try {
            const repository = await this.getRepository(orgId);
            return repository.getById(id);
        }
        catch (error) {
            rtLogService.error(`Error fetching ${this.opts.collection} by id`, { orgId, id, collection: this.opts.collection, error });
            throw error;
        }
    }
    /**
     * Fresh read for optimistic concurrency.
     *
     * `getById` may serve a stale InMemory entry for the whole browser session; an org
     * `config-repository` override can re-enable cache even when the descriptor defaults
     * to NoCache. Invalidating before read is what keeps version detection honest.
     */
    async getByIdFresh(orgId, id) {
        try {
            const repository = await this.getRepository(orgId);
            await repository.invalidateCache(id);
            return repository.getById(id);
        }
        catch (error) {
            rtLogService.error(`Error fetching fresh ${this.opts.collection} by id`, {
                orgId,
                id,
                collection: this.opts.collection,
                error,
            });
            throw error;
        }
    }
    /**
     * Field marked `unique: true` on the entity descriptor, if any.
     * Missing registry entry → logged (or thrown in strict mode) via getDescriptorForRule.
     */
    resolveUniqueCodeField() {
        const descriptor = getDescriptorForRule(this.opts.entity, 'unique-code');
        return descriptor?.fields.find((f) => f.unique === true)?.name;
    }
    async assertUniqueCode(orgId, data, currentId) {
        const field = this.resolveUniqueCodeField();
        if (!field)
            return;
        const raw = data[field];
        if (raw == null)
            return;
        const entityLabel = this.opts.uniqueCodeEntityLabel ?? 'um registro';
        await assertCodeUniquePerOrg(this, orgId, Number(raw), currentId, { entityLabel });
    }
    /**
     * PG unique index race (request 0322): translate `/pg` `unique_violation` into the
     * same {@link ValidationException} as {@link assertUniqueCode}.
     */
    mapUniqueCodeViolation(error, data) {
        const field = this.resolveUniqueCodeField();
        return validationExceptionFromUniqueCodeViolation(error, {
            uniqueField: field,
            entityLabel: this.opts.uniqueCodeEntityLabel ?? 'um registro',
            attemptedCode: field ? data[field] : undefined,
        });
    }
    /**
     * Merge caller DraftContext with extras from {@link OrgScopedCrudOptions.loadValidationContext}.
     * Passes `ctx` into the loader so it can skip work already done by the caller
     * (or by a subclass prepare step). Caller `extra` keys win on merge.
     */
    async resolveValidationCtx(orgId, data, id, ctx) {
        if (!this.opts.loadValidationContext) {
            return ctx ?? { orgId };
        }
        const loaded = await this.opts.loadValidationContext(orgId, data, id, ctx);
        if (!loaded) {
            return ctx ?? { orgId };
        }
        return {
            orgId: ctx?.orgId ?? orgId,
            orgParams: ctx?.orgParams,
            moduleConfig: ctx?.moduleConfig,
            extra: { ...loaded, ...ctx?.extra },
        };
    }
    /**
     * L0 (descriptor) then L1 (`opts.validate`); L1 wins on the same field.
     * `mode: 'create' | 'full'` — all writable fields (empty autoNumber skipped).
     * `mode: 'partial'` — only keys present on `data` (empty `{}` → no L0).
     */
    async runWriteValidation(orgId, data, mode, ctx) {
        let l0;
        if (this.opts.descriptorValidation !== false) {
            const descriptor = getDescriptorForRule(this.opts.entity, 'descriptor-validation');
            if (descriptor) {
                const activeGroups = await orgEntityConfigService.getActiveGroups(orgId);
                const fields = mode === 'partial'
                    ? descriptorFieldsForPartialUpdateValidation(descriptor, data, activeGroups)
                    : descriptorFieldsForCreateValidation(descriptor, data, activeGroups);
                if (fields.length > 0) {
                    const errors = runDescriptorValidation(descriptor, data, fields);
                    if (Object.keys(errors).length > 0)
                        l0 = errors;
                }
            }
        }
        const l1 = this.opts.validate?.(data, ctx);
        const merged = mergeErrors(l1, l0);
        if (merged)
            throw new EntityValidationError(merged);
    }
    /**
     * Compare without reading — for a service that already holds the record and must
     * check EARLY (before child writes). See request 0236 §6.
     */
    assertVersion(persisted, expected) {
        if (!this.opts.versioned)
            return;
        if (expected === VERSION_BYPASS)
            return;
        const current = toVersion(persisted.version);
        if (toVersion(expected) !== current) {
            throw new ConcurrentModificationException(toVersion(expected), current);
        }
    }
    /**
     * Audit scope for a service that owns its own {@link WriteBatch} (request 0255).
     * Notes the header repository destination. Returns `undefined` when not audited.
     */
    async beginAggregateAudit(orgId, aggregate, ctx) {
        if (!this.opts.audited)
            return undefined;
        const repository = await this.getRepository(orgId);
        const scope = new AggregateAuditScope(new AuditRecorder(orgId, ctx), {
            entity: this.opts.entity,
            id: aggregate.id,
            version: aggregate.version,
            record: aggregate.record,
        });
        scope.noteDestination(repository);
        return scope;
    }
    /**
     * Commit owned by the aggregate service: same atomic-vs-split rule as
     * {@link withAggregateVersion}. Without a scope, plain {@link commitWriteBatch}.
     */
    async commitAggregateBatch(orgId, batch, audit) {
        if (!audit) {
            await commitWriteBatch(batch);
            return;
        }
        await this.commitAuditedBatch(orgId, batch, audit.recorder, audit.destinationKeys);
    }
    /**
     * Persist children of a versioned aggregate under the parent's version (spec 0045
     * phase 2b / request 0238 / 0243). Per call: one check, one {@link WriteBatch},
     * children + parent increment in the same commit, one fresh re-read — never per
     * child row.
     *
     * Sequence: early `assertVersion` → `createWriteBatch` → `write(batch, audit?)` →
     * empty parent update with `VERSION_BYPASS` on the same batch → commit (atomic or
     * split when {@link OrgScopedCrudOptions.audited}) → fresh parent. Bypass on the
     * increment is intentional: the version value is computed when enqueueing (before
     * commit); refusing the bump after children were already committed would leave
     * them saved without bumping the parent.
     *
     * On PostgresRest the parent bump carries `ifMatch` (request 0247), so the
     * check-to-commit window is closed inside the atomic `_batch` transaction.
     * Firestore (and sources without atomic batch) still ignore `ifMatch` — see
     * the comment at the enqueue site and spec 0045 §9.9.
     *
     * When `audited: true`, this method owns atomic vs split commit for children +
     * parent + field-audit log (request 0250 / 0255 via {@link beginAggregateAudit} +
     * {@link commitAggregateBatch}). Callers that ignore the optional `audit`
     * argument (OP / override) keep the pre-0250 behaviour.
     */
    async withAggregateVersion(orgId, id, expected, write) {
        if (!this.opts.versioned) {
            throw new Error(`${this.opts.collection}: withAggregateVersion requires versioned: true`);
        }
        const existing = await this.getByIdFresh(orgId, id);
        if (!existing) {
            throw new Error(`${this.opts.collection} ${id} not found`);
        }
        this.assertVersion(existing, expected);
        const batch = createWriteBatch();
        const auditScope = await this.beginAggregateAudit(orgId, {
            id,
            version: toVersion(existing.version) + 1,
            record: existing,
        });
        const result = await write(batch, auditScope);
        // Empty update bumps version (+ audit stamps) in the same batch as children.
        // VERSION_BYPASS: compare already happened above; increment is from the
        // fresh current value at enqueue time (readBeforeWrite). ifMatch still
        // guards the bump against another concurrent writer (request 0247).
        // Field audit: empty payload ⇒ empty diff ⇒ no AuditOperation (request 0249).
        await this.update(orgId, id, {}, undefined, {
            version: VERSION_BYPASS,
            batch,
            aggregateAudit: auditScope,
        });
        await this.commitAggregateBatch(orgId, batch, auditScope);
        const parent = await this.getByIdFresh(orgId, id);
        if (!parent) {
            throw new Error(`${this.opts.collection} ${id} not found after aggregate write`);
        }
        return { result, parent };
    }
    /**
     * Fresh read-before-write shared by optimistic concurrency and field audit (spec 0046).
     *
     * Reads when `versioned` and/or `audited`. Version semantics are unchanged: missing
     * `opts.version` on a versioned entity still throws; `ifMatch` still uses `current`.
     */
    async readBeforeWrite(orgId, id, opts) {
        if (!this.opts.versioned && !this.opts.audited) {
            return { persisted: null };
        }
        if (this.opts.versioned && opts?.version === undefined) {
            throw new Error(`${this.opts.collection}: update de entidade versionada exige version (use VERSION_BYPASS para escrita de sistema).`);
        }
        const persisted = await this.getByIdFresh(orgId, id);
        if (!persisted) {
            throw new Error(`${this.opts.collection} ${id} not found`);
        }
        if (!this.opts.versioned) {
            return { persisted };
        }
        const rawVersion = persisted.version;
        // Pre-versioning / sync-bypass rows store NULL — toVersion treats as 0 for
        // in-memory compare, but ifMatch must send null (system-api → IS NULL).
        const persistedNull = rawVersion === null || rawVersion === undefined;
        const current = toVersion(rawVersion);
        if (opts.version !== VERSION_BYPASS && toVersion(opts.version) !== current) {
            throw new ConcurrentModificationException(toVersion(opts.version), current);
        }
        return { persisted, version: { current, next: current + 1, persistedNull } };
    }
    /** ifMatch value: null when the row still has NULL version (legacy / sync-bypass). */
    ifMatchVersion(version) {
        return version.persistedNull ? null : version.current;
    }
    stripClientVersion(data) {
        if (!('version' in data))
            return data;
        const { version: _ignored, ...rest } = data;
        return rest;
    }
    /**
     * Caller-batch path: enqueue mutation then audit on the same batch. The **caller** commits.
     *
     * Used by header create/update/patch/delete when the caller already owns the batch
     * **without** an {@link AggregateAuditScope}. Aggregate-owned batches use
     * `aggregateAudit` + {@link commitAggregateBatch} instead (request 0255).
     */
    async enqueueAuditedMutation(orgId, batch, mutate, recordAudit, auditCtx) {
        mutate(batch);
        if (!this.opts.audited)
            return;
        const recorder = new AuditRecorder(orgId, auditCtx);
        recordAudit(recorder);
        await recorder.enqueue(batch);
    }
    /**
     * Register on an aggregate scope without enqueueing — the owner commits later.
     */
    recordOnAggregateScope(repository, scope, recordAudit) {
        if (!this.opts.audited)
            return;
        scope.noteDestination(repository);
        recordAudit(scope.recorder);
    }
    /**
     * Local-batch path (no caller `opts.batch`): single entity destination + audit.
     */
    async commitLocalAudited(orgId, repository, mutationBatch, recorder) {
        const entityKey = repository.getWriteBatchSupport?.()?.destinationKey ?? null;
        await this.commitAuditedBatch(orgId, mutationBatch, recorder, [entityKey]);
    }
    /**
     * Shared atomic-vs-split rule for field audit (spec 0046 §10.1 / request 0250).
     *
     * All business `destinationKeys` plus the audit destination must be non-null and equal
     * for one atomic commit; otherwise commit the mutation alone, then best-effort the log
     * so a PG/log failure cannot surface as an error on an already-persisted save.
     */
    async commitAuditedBatch(orgId, mutationBatch, recorder, destinationKeys) {
        if (!recorder.hasPending) {
            await commitWriteBatch(mutationBatch);
            return;
        }
        const auditKey = await recorder.resolveDestinationKey();
        const canAtomic = auditKey != null &&
            destinationKeys.length > 0 &&
            destinationKeys.every((key) => key != null && key === auditKey);
        if (canAtomic) {
            await recorder.enqueue(mutationBatch);
            await commitWriteBatch(mutationBatch);
            return;
        }
        await commitWriteBatch(mutationBatch);
        try {
            const auditBatch = createWriteBatch();
            await recorder.enqueue(auditBatch);
            await commitWriteBatch(auditBatch);
        }
        catch (error) {
            const meta = recorder.firstPendingMeta;
            rtLogService.error(`[field-audit] write failed after entity save — save succeeded without an audit log`, {
                orgId,
                entity: meta?.entity ?? this.opts.entity,
                recordId: meta?.recordId,
                error,
            });
            rtLogService.dispatchMonitorEvent({
                source: MonitorEventSource.DATA_ACCESS,
                severity: MonitorEventSeverity.ERROR,
                type: 'field-audit-write-failed',
                payload: {
                    orgId,
                    entity: meta?.entity ?? this.opts.entity,
                    recordId: meta?.recordId,
                },
            });
        }
    }
    async create(orgId, data, ctx, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        try {
            const resolvedCtx = await this.resolveValidationCtx(orgId, data, undefined, ctx);
            await this.runWriteValidation(orgId, data, 'create', resolvedCtx);
            await this.assertUniqueCode(orgId, data, undefined);
            const payload = this.stripClientVersion(data);
            let managed = payload;
            if (this.opts.versioned) {
                managed = { ...managed, version: 1 };
            }
            if (this.opts.lifecycle) {
                const inactivationDate = data.inactivationDate ?? null;
                managed = { ...managed, active: lifecycleService.isActive({ inactivationDate }), inactivationDate };
            }
            if (this.opts.auditable !== false) {
                managed = auditService.stampOnCreate(managed);
            }
            const repository = await this.getRepository(orgId);
            const recordCreateAudit = (recorder, recordId) => {
                const changes = diffForAudit(null, payload, this.opts.entity);
                const aggregate = opts?.aggregateAudit?.aggregate ?? {
                    entity: this.opts.entity,
                    id: recordId,
                    version: this.opts.versioned ? 1 : null,
                    record: { ...payload, id: recordId },
                };
                recorder.record({
                    entity: this.opts.entity,
                    recordId,
                    op: 'create',
                    changes,
                    aggregate,
                });
            };
            if (opts?.batch) {
                const id = opts.batch.create(repository, managed);
                if (opts.aggregateAudit) {
                    this.recordOnAggregateScope(repository, opts.aggregateAudit, (recorder) => recordCreateAudit(recorder, id));
                }
                else if (this.opts.audited) {
                    const recorder = new AuditRecorder(orgId, opts.audit);
                    recordCreateAudit(recorder, id);
                    await recorder.enqueue(opts.batch);
                }
                const created = { ...managed, id };
                // Batch has no post-commit hook — emit now with batched:true (0352).
                logCrudActivity({
                    action: 'record.create',
                    entity: this.opts.entity,
                    entityId: id,
                    record: created,
                    durationMs: Date.now() - started,
                    batched: true,
                });
                return created;
            }
            if (this.opts.audited) {
                const batch = createWriteBatch();
                const id = batch.create(repository, managed);
                const recorder = new AuditRecorder(orgId, opts?.audit);
                recordCreateAudit(recorder, id);
                await this.commitLocalAudited(orgId, repository, batch, recorder);
                const fresh = await this.getByIdFresh(orgId, id);
                const created = fresh ?? { ...managed, id };
                logCrudActivity({
                    action: 'record.create',
                    entity: this.opts.entity,
                    entityId: id,
                    record: created,
                    durationMs: Date.now() - started,
                });
                return created;
            }
            const created = await repository.create(managed);
            logCrudActivity({
                action: 'record.create',
                entity: this.opts.entity,
                entityId: created.id,
                record: created,
                durationMs: Date.now() - started,
            });
            return created;
        }
        catch (error) {
            const mapped = this.mapUniqueCodeViolation(error, data);
            const thrown = mapped ?? error;
            logCrudActivity({
                action: 'record.create',
                entity: this.opts.entity,
                record: data,
                outcome: 'error',
                durationMs: Date.now() - started,
                batched,
                error: thrown,
            });
            if (mapped)
                throw mapped;
            if (!(error instanceof EntityValidationError) &&
                !(error instanceof ValidationException) &&
                !(error instanceof ConcurrentModificationException)) {
                rtLogService.error(`Error creating ${this.opts.collection}`, { orgId, collection: this.opts.collection, error });
            }
            throw error;
        }
    }
    async update(orgId, id, data, ctx, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        let fields;
        let recordForLabel = data;
        try {
            const resolvedCtx = await this.resolveValidationCtx(orgId, data, id, ctx);
            await this.runWriteValidation(orgId, data, 'partial', resolvedCtx);
            await this.assertUniqueCode(orgId, data, id);
            const payload = this.stripClientVersion(data);
            let managed = payload;
            const before = await this.readBeforeWrite(orgId, id, opts);
            if (before.persisted) {
                recordForLabel = { ...before.persisted, ...payload, id };
                fields = safeChangedFieldNames(before.persisted, payload, this.opts.entity);
            }
            if (before.version !== undefined) {
                managed = { ...managed, version: before.version.next };
            }
            if (this.opts.lifecycle && 'inactivationDate' in data) {
                const inactivationDate = data.inactivationDate ?? null;
                managed = { ...managed, active: lifecycleService.isActive({ inactivationDate }) };
            }
            if (this.opts.auditable !== false) {
                managed = auditService.stampOnUpdate(managed);
            }
            const repository = await this.getRepository(orgId);
            const enqueueUpdate = (batch) => {
                if (before.version !== undefined) {
                    batch.update(repository, id, managed, {
                        ifMatch: { version: this.ifMatchVersion(before.version) },
                    });
                }
                else {
                    batch.update(repository, id, managed);
                }
            };
            const recordUpdateAudit = (recorder) => {
                const changes = diffForAudit((before.persisted ?? {}), payload, this.opts.entity);
                const aggregate = opts?.aggregateAudit?.aggregate ?? {
                    entity: this.opts.entity,
                    id,
                    version: before.version?.next ?? null,
                    record: {
                        ...before.persisted,
                        ...payload,
                        id,
                    },
                };
                recorder.record({
                    entity: this.opts.entity,
                    recordId: id,
                    op: 'update',
                    changes,
                    aggregate,
                });
            };
            if (opts?.batch) {
                if (opts.aggregateAudit) {
                    enqueueUpdate(opts.batch);
                    this.recordOnAggregateScope(repository, opts.aggregateAudit, recordUpdateAudit);
                }
                else {
                    await this.enqueueAuditedMutation(orgId, opts.batch, enqueueUpdate, recordUpdateAudit, opts.audit);
                }
            }
            else if (this.opts.audited) {
                const batch = createWriteBatch();
                enqueueUpdate(batch);
                const recorder = new AuditRecorder(orgId, opts?.audit);
                recordUpdateAudit(recorder);
                await this.commitLocalAudited(orgId, repository, batch, recorder);
            }
            else {
                await repository.update(id, managed);
            }
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                durationMs: Date.now() - started,
                fields,
                batched,
            });
        }
        catch (error) {
            const mapped = this.mapUniqueCodeViolation(error, data);
            const thrown = mapped ?? error;
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                outcome: 'error',
                durationMs: Date.now() - started,
                fields,
                batched,
                error: thrown,
            });
            if (mapped)
                throw mapped;
            if (!(error instanceof EntityValidationError) &&
                !(error instanceof ValidationException) &&
                !(error instanceof ConcurrentModificationException)) {
                rtLogService.error(`Error updating ${this.opts.collection}`, { orgId, id, collection: this.opts.collection, error });
            }
            throw error;
        }
    }
    /**
     * Persists only `patch`, validating the merged record (`validateAgainst`).
     *
     * Exists because `update` validates the payload it receives: a partial patch would fail on
     * required fields, forcing callers to resend the full entity — which overwrites concurrent
     * edits to fields they did not touch.
     */
    async patch(orgId, id, patch, validateAgainst, ctx, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        let fields;
        let recordForLabel = patch;
        try {
            const resolvedCtx = await this.resolveValidationCtx(orgId, validateAgainst, id, ctx);
            await this.runWriteValidation(orgId, validateAgainst, 'full', resolvedCtx);
            const payload = this.stripClientVersion(patch);
            let managed = payload;
            const before = await this.readBeforeWrite(orgId, id, opts);
            if (before.persisted) {
                recordForLabel = { ...before.persisted, ...payload, id };
                fields = safeChangedFieldNames(before.persisted, payload, this.opts.entity);
            }
            if (before.version !== undefined) {
                managed = { ...managed, version: before.version.next };
            }
            if (this.opts.lifecycle && 'inactivationDate' in patch) {
                const inactivationDate = patch.inactivationDate ?? null;
                managed = { ...managed, active: lifecycleService.isActive({ inactivationDate }) };
            }
            if (this.opts.auditable !== false) {
                managed = auditService.stampOnUpdate(managed);
            }
            const repository = await this.getRepository(orgId);
            const enqueueUpdate = (batch) => {
                if (before.version !== undefined) {
                    batch.update(repository, id, managed, {
                        ifMatch: { version: this.ifMatchVersion(before.version) },
                    });
                }
                else {
                    batch.update(repository, id, managed);
                }
            };
            const recordPatchAudit = (recorder) => {
                const changes = diffForAudit((before.persisted ?? {}), payload, this.opts.entity);
                const aggregate = opts?.aggregateAudit?.aggregate ?? {
                    entity: this.opts.entity,
                    id,
                    version: before.version?.next ?? null,
                    record: {
                        ...before.persisted,
                        ...payload,
                        id,
                    },
                };
                recorder.record({
                    entity: this.opts.entity,
                    recordId: id,
                    op: 'update',
                    changes,
                    aggregate,
                });
            };
            if (opts?.batch) {
                if (opts.aggregateAudit) {
                    enqueueUpdate(opts.batch);
                    this.recordOnAggregateScope(repository, opts.aggregateAudit, recordPatchAudit);
                }
                else {
                    await this.enqueueAuditedMutation(orgId, opts.batch, enqueueUpdate, recordPatchAudit, opts.audit);
                }
            }
            else if (this.opts.audited) {
                const batch = createWriteBatch();
                enqueueUpdate(batch);
                const recorder = new AuditRecorder(orgId, opts?.audit);
                recordPatchAudit(recorder);
                await this.commitLocalAudited(orgId, repository, batch, recorder);
            }
            else {
                await repository.update(id, managed);
            }
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                durationMs: Date.now() - started,
                fields,
                batched,
            });
        }
        catch (error) {
            const mapped = this.mapUniqueCodeViolation(error, patch);
            const thrown = mapped ?? error;
            logCrudActivity({
                action: 'record.update',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                outcome: 'error',
                durationMs: Date.now() - started,
                fields,
                batched,
                error: thrown,
            });
            if (mapped)
                throw mapped;
            if (!(error instanceof EntityValidationError) &&
                !(error instanceof ValidationException) &&
                !(error instanceof ConcurrentModificationException)) {
                rtLogService.error(`Error patching ${this.opts.collection}`, { orgId, id, collection: this.opts.collection, error });
            }
            throw error;
        }
    }
    async delete(orgId, id, opts) {
        const started = Date.now();
        const batched = Boolean(opts?.batch);
        let recordForLabel = { id };
        try {
            let snapshot;
            let freshRecord;
            if (this.opts.audited) {
                const fresh = await this.getByIdFresh(orgId, id);
                if (fresh) {
                    freshRecord = fresh;
                    recordForLabel = freshRecord;
                    snapshot = buildAuditSnapshot(freshRecord, this.opts.entity);
                }
            }
            const repository = await this.getRepository(orgId);
            const enqueueDelete = (batch) => {
                batch.delete(repository, id);
            };
            const recordDeleteAudit = (recorder) => {
                const aggregate = opts?.aggregateAudit?.aggregate ?? {
                    entity: this.opts.entity,
                    id,
                    version: null,
                    record: freshRecord ?? { id },
                };
                recorder.record({
                    entity: this.opts.entity,
                    recordId: id,
                    op: 'delete',
                    changes: [],
                    snapshot: snapshot ?? undefined,
                    aggregate,
                });
            };
            if (opts?.batch) {
                if (opts.aggregateAudit) {
                    enqueueDelete(opts.batch);
                    this.recordOnAggregateScope(repository, opts.aggregateAudit, recordDeleteAudit);
                }
                else {
                    await this.enqueueAuditedMutation(orgId, opts.batch, enqueueDelete, recordDeleteAudit, opts.audit);
                }
            }
            else if (this.opts.audited) {
                const batch = createWriteBatch();
                enqueueDelete(batch);
                const recorder = new AuditRecorder(orgId, opts?.audit);
                recordDeleteAudit(recorder);
                await this.commitLocalAudited(orgId, repository, batch, recorder);
            }
            else {
                await repository.delete(id);
            }
            logCrudActivity({
                action: 'record.delete',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                durationMs: Date.now() - started,
                batched,
            });
        }
        catch (error) {
            logCrudActivity({
                action: 'record.delete',
                entity: this.opts.entity,
                entityId: id,
                record: recordForLabel,
                outcome: 'error',
                durationMs: Date.now() - started,
                batched,
                error,
            });
            rtLogService.error(`Error deleting ${this.opts.collection}`, { orgId, id, collection: this.opts.collection, error });
            throw error;
        }
    }
}
//# sourceMappingURL=OrgScopedCrudService.js.map